import { AnimatePresence, motion } from 'motion/react';
import type { AllHTMLAttributes, FocusEvent, ReactNode } from 'react';
import { forwardRef, useMemo, useState } from 'react';

import { atoms } from '../../css/atoms/atoms.css';
import { Box } from '../Box/Box';
import { FieldMessage } from '../FieldMessage/FieldMessage';
import { Adornment } from './Adornment';
import { ClearButton } from './ClearButton';
import {
  cautionHoverStyle,
  critical25HoverStyle,
  criticalHoverStyle,
  defaultHoverStyle,
  fieldsetStyle,
  inputStyle,
  labelStyle,
  legendStyle,
  spanStyle,
  wrapperStyle,
} from './TextField.css';

type InputProps = AllHTMLAttributes<HTMLInputElement>;

type Tone = 'positive' | 'neutral' | 'critical' | 'caution' | 'neutral25' | 'critical25';

type Size = 'small' | 'medium' | 'large' | 'large25';

export interface TextFieldProps {
  id?: string;
  type?: 'email' | 'number' | 'password' | 'tel' | 'text' | 'url' | 'date';
  /**
   * Controls the label rendered inside TextField.
   * The label translates to the top-left of the component when the field is focused.
   */
  label?: InputProps['label'];
  labelIcon?: ReactNode;
  /**
   * Controls the text rendered underneath the TextField
   */
  message?: string;
  /**
   * Controls the colour of the field.
   *
   * Use this prop to express different field states:
   * - `'neutral'` (default)
   * - `'critical'`
   * - `'positive'`
   * - `'caution'`
   */
  tone?: Tone;
  /**
   * Use this prop to overlay an element at the end of the TextField.
   */
  adornment?: ReactNode;
  /**
   * Use this prop to provide a position of the adornment. Default position is `right`.
   */
  adornmentPosition?: 'right' | 'left';
  /**
   * Controls the visual size of the TextField
   */
  size?: Size;
  autoComplete?: InputProps['autoComplete'];
  autoCapitalize?: InputProps['autoCapitalize'];
  autoCorrect?: InputProps['autoCorrect'];
  autoFocus?: InputProps['autoFocus'];
  disabled?: boolean;
  inputMode?: InputProps['inputMode'];
  value?: InputProps['value'];
  placeholder?: InputProps['placeholder'];
  step?: InputProps['step'];
  required?: boolean;
  min?: InputProps['min'];
  max?: InputProps['max'];
  maxLength?: InputProps['maxLength'];
  readOnly?: boolean;
  onChange: NonNullable<InputProps['onChange']>;
  /**
   * Use this prop to render a clear button inside the TextField.
   */
  onClear?: () => void;
  onBlur?: InputProps['onBlur'];
  onPaste?: InputProps['onPaste'];
  onFocus?: InputProps['onFocus'];
  onKeyDown?: InputProps['onKeyDown'];
  onKeyUp?: InputProps['onKeyUp'];
  'aria-label'?: InputProps['aria-label'];
  'aria-describedby'?: InputProps['aria-describedby'];
  'aria-required'?: InputProps['aria-required'];
  'aria-labelledby'?: InputProps['aria-labelledby'];
}

/**
 * @deprecated Use Mantine's TextInput component instead.
 * Import: import { TextInput } from '@mantine/core'
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      id,
      value,
      type,
      onClear,
      onBlur,
      onFocus,
      label,
      labelIcon,
      message,
      tone,
      disabled,
      adornment,
      adornmentPosition = 'right',
      size = 'medium',
      readOnly = false,
      ...restProps
    },
    forwardedRef,
  ) => {
    const [focused, setFocused] = useState(false);

    const handleOnFocus = (event: FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      if (onFocus) {
        onFocus(event);
      }
    };

    const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      if (onBlur) {
        onBlur(event);
      }
    };

    const hasValue = typeof value === 'string' && value.length > 0;

    const hoverStyle = useMemo(() => {
      switch (true) {
        case focused || disabled:
          return undefined;
        case tone === 'critical':
          return criticalHoverStyle;
        case tone === 'critical25':
          return critical25HoverStyle;
        case tone === 'caution':
          return cautionHoverStyle;
        default:
          return defaultHoverStyle;
      }
    }, [focused, disabled, tone]);

    const isLeftAdorned = adornmentPosition === 'left' && adornment !== undefined;

    return (
      <Box display="flex" flexDirection="column">
        <div className={wrapperStyle}>
          {label && (
            <label
              htmlFor={id}
              className={labelStyle({
                size,
                focused: focused || hasValue || type === 'date',
                disabled,
              })}
            >
              {label}
              {labelIcon}
            </label>
          )}
          <div className={hoverStyle}>
            <input
              id={id}
              value={value}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              className={inputStyle({
                size,
                isLeftAdorned,
              })}
              type={type}
              disabled={disabled}
              ref={forwardedRef}
              readOnly={readOnly}
              {...restProps}
            />
            <fieldset
              className={fieldsetStyle({
                focused,
                tone,
              })}
            >
              <legend
                className={legendStyle({
                  focused: focused || hasValue || type === 'date',
                  large25: size === 'large25',
                })}
              >
                {label && (
                  <span className={spanStyle}>
                    {label}
                    {labelIcon}
                  </span>
                )}
              </legend>
            </fieldset>
          </div>

          {/* Clear Button */}
          <AnimatePresence mode="wait">
            {onClear && value && (
              <Adornment position="right">
                {
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ClearButton onClick={onClear} disabled={disabled} />
                  </motion.div>
                }
              </Adornment>
            )}
          </AnimatePresence>

          {/* End Adornment */}
          {!onClear && adornment && <Adornment position={adornmentPosition}>{adornment}</Adornment>}
        </div>
        {message && (
          <span
            className={atoms({
              paddingTop: 'xsmall',
            })}
          >
            <FieldMessage id={id} tone={tone} message={message} />
          </span>
        )}
      </Box>
    );
  },
);

TextField.displayName = 'TextField';
